/**
 * myDayFocusApi.js
 *
 * Network client of the prioritised part of the "Mai napom" briefing (`salesFocus.controller.js`):
 * the focus block, the AI day plan and the AI mail-reply draft. Raw HTTP like `salesFollowUpApi` (routes newer
 * than the shipped prod spec). Errors carry `status` (402 / 403 = no AI credits, 404 / 405 = server too old).
 *
 * @module helpers/myDayFocusApi
 */

import {apiFetch} from './WorkSheetContext';
import {normalizeDraft, normalizeFocus, normalizePlan} from './myDayFocus';

/**
 * GET the focus block. Resolves the EMPTY block (never throws) when the server does not know the route yet.
 * @returns {Promise<object>} normalized focus (`available: false` on failure).
 */
export const fetchFocus = async () => {
    try {
        return normalizeFocus(await apiFetch('/crm/follow-ups/focus', {skipCache: true}));
    } catch (_e) {
        return normalizeFocus(null);
    }
};

/**
 * POST the AI day plan (spends AI credits on a cache miss).
 * @param {object[]} items rows of `toPlanItems`. @param {string} locale UI language.
 * @returns {Promise<object>} normalized plan.
 */
export const analyzeFocusPlan = async (items, locale) => normalizePlan(await apiFetch('/crm/follow-ups/focus/analyze', {
    method: 'POST', body: JSON.stringify({items, locale}),
}));

/**
 * POST an AI reply draft for one of the user's own inbox messages (spends AI credits; never sends).
 * @param {{messageId: string, locale?: string, tone?: string, instruction?: string}} params params.
 * @returns {Promise<object|null>} normalized draft, or null when the AI returned nothing readable.
 */
export const draftMailReply = async ({messageId, locale, tone = 'professional', instruction = ''}) => normalizeDraft(
    await apiFetch('/crm/follow-ups/focus/reply-draft', {
        method: 'POST', body: JSON.stringify({messageId, locale, tone, instruction}),
    }),
);
